'use client';

import { Compass, PenTool, Code2, LineChart } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { useScrollReveal } from '@/app/hooks/use-scroll-reveal';

const STEPS = [
  { num: '01', icon: Compass, title: 'Discover', desc: 'We dig into your community, your operations, and the friction your members feel today.' },
  { num: '02', icon: PenTool, title: 'Design', desc: 'Wireframes, prototypes, and accessibility-first interfaces. Tested with real users.' },
  { num: '03', icon: Code2, title: 'Build', desc: 'Production-ready software on Next.js + TypeScript. Shipped in small, reviewable PRs.' },
  { num: '04', icon: LineChart, title: 'Operate', desc: 'We don\u2019t hand off and walk away. We monitor, support, and improve continuously.' },
] as const;

export function Approach() {
  const { ref, isVisible } = useScrollReveal();
  const prefersReducedMotion = useReducedMotion();

  return (
    <section className="bg-white py-[clamp(80px,10vw,140px)]">
      <div ref={ref} className="mx-auto max-w-[1200px] px-5 md:px-10 lg:px-0">
        {/* Section header */}
        <div className="mb-10 max-w-[720px] md:mb-16">
          <div className="flex items-center gap-3">
            <span className="h-px w-6 bg-sandron-ink-300" aria-hidden="true" />
            <span className="font-body text-xs font-semibold uppercase tracking-[0.12em] text-sandron-ink-500">
              How we work
            </span>
          </div>
          <h2 className="mt-4 font-display text-[clamp(32px,4vw,56px)] font-bold leading-[1.1] tracking-[-0.02em] text-sandron-ink-900 [text-wrap:balance]">
            From discovery to operation, in lockstep with you.
          </h2>
          <p className="mt-4 max-w-[600px] text-lg leading-[1.55] text-sandron-ink-700">
            Every engagement follows a clear path — but we stay flexible enough
            to meet communities where they are.
          </p>
        </div>

        {/* Steps grid with connector */}
        <div className="relative grid gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
          {/* Desktop connector line (behind badges) */}
          <div
            className="pointer-events-none absolute left-[28px] right-[28px] top-[28px] hidden h-0.5 bg-sandron-ink-100 lg:block"
            aria-hidden="true"
          />
          {/* Animated fill line */}
          <motion.div
            className="pointer-events-none absolute left-[28px] right-[28px] top-[28px] hidden h-0.5 origin-left bg-spacenest-primary lg:block"
            initial={{ scaleX: 0 }}
            animate={isVisible ? { scaleX: 1 } : { scaleX: 0 }}
            transition={{
              duration: prefersReducedMotion ? 0 : 1.2,
              delay: 0.3,
              ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
            }}
            aria-hidden="true"
          />

          {/* Mobile connector line (vertical) */}
          <div
            className="pointer-events-none absolute bottom-0 left-[27px] top-0 w-0.5 bg-sandron-ink-100 sm:hidden"
            aria-hidden="true"
          />

          {STEPS.map((step, index) => (
            <motion.div
              key={step.num}
              initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
              animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0 }}
              transition={{
                duration: prefersReducedMotion ? 0.15 : 0.6,
                delay: index * 0.12,
                ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
              }}
              className="group relative flex flex-col gap-5"
            >
              {/* Step number badge */}
              <motion.div
                initial={{ opacity: 0, scale: prefersReducedMotion ? 1 : 0.85 }}
                animate={isVisible ? { opacity: 1, scale: 1 } : { opacity: 0 }}
                transition={{
                  duration: prefersReducedMotion ? 0.15 : 0.5,
                  delay: index * 0.12 + 0.1,
                  ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
                }}
                className="relative z-10 flex h-14 w-14 items-center justify-center rounded-full border-2 border-sandron-ink-100 bg-white"
              >
                <span className="font-display text-lg font-bold text-sandron-ink-900">
                  {step.num}
                </span>
              </motion.div>

              {/* Icon + title */}
              <div>
                <step.icon
                  size={24}
                  strokeWidth={2}
                  className="text-sandron-ink-500 transition-colors duration-200 group-hover:text-spacenest-primary"
                  aria-hidden="true"
                />
                <h3 className="mt-2 font-display text-[22px] font-bold leading-[1.2] text-sandron-ink-900">
                  {step.title}
                </h3>
              </div>

              {/* Description */}
              <p className="text-[15px] leading-[1.6] text-sandron-ink-700">
                {step.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
